import { useMemo } from 'react'

import { StaffType } from '@/utils/onboarding/type'
import { WaitingRoom } from '@/core/models/waiting-room.model'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'

/**
 * Whether the Desmos billing UI ("facturation") should be shown to the current
 * user for a given waiting room.
 *
 * Billing is opt-in per waiting room and per staff role, configured on the
 * waiting-room form (`paymentDialogConfig`). Both the live consultation and the
 * consultation history read it through this hook so a room that does not bill
 * through Desmos never surfaces it in one place and hides it in the other.
 *
 * Returns false while the room is still loading, which keeps the card from
 * flashing in for users who should not see it.
 */
export function useBillingDialogEnabled(waitingRoom?: Pick<WaitingRoom, 'paymentDialogConfig'>): boolean {
  const authUser = useAppSelector(selectAuthUser)

  return useMemo(() => {
    if (!waitingRoom?.paymentDialogConfig?.enabled) return false
    if (!isStaff(authUser) || !authUser?.status) return false
    return waitingRoom.paymentDialogConfig.showForRoles.includes(authUser.status as StaffType)
  }, [waitingRoom?.paymentDialogConfig, authUser])
}
